import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import Icon from '../../components/AppIcon';

const SUPER_ADMIN_EMAILS = ['mazine@lhiam.com'];

const ProvisionTenantPanel = ({ userProfile }) => {
  const [orgName, setOrgName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFullName, setAdminFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleProvision = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) throw new Error('Session expirée — veuillez vous reconnecter.');

      const { data, error: fnError } = await supabase?.functions?.invoke('provision-tenant', {
        body: {
          orgName: orgName?.trim(),
          adminEmail: adminEmail?.trim()?.toLowerCase(),
          adminFullName: adminFullName?.trim(),
        },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (fnError) throw new Error(fnError.message || 'Erreur de la fonction Edge');
      if (data && !data?.success) throw new Error(data.error || 'Échec de la provision');

      setResult(data);
      setOrgName('');
      setAdminEmail('');
      setAdminFullName('');
    } catch (err) {
      setError(err?.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const isValid = orgName?.trim() && adminEmail?.trim() && adminFullName?.trim();

  return (
    <div className="bg-card border border-amber-300 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-amber-200 bg-amber-50 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Icon name="Building2" size={16} className="text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-[#2C3E50]">Provisionner un tenant</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Crée une nouvelle organisation et invite son premier administrateur.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="px-5 py-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-[#2C3E50] mb-1">Nom de l'organisation</label>
          <input
            type="text"
            value={orgName}
            onChange={(e) => setOrgName(e?.target?.value)}
            placeholder="Ex: Construction Dupont Inc."
            disabled={loading}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-[#2C3E50] placeholder-[#9CA3AF] disabled:opacity-50"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#2C3E50] mb-1">Courriel de l'admin</label>
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e?.target?.value)}
            placeholder="admin@organisation.com"
            disabled={loading}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-[#2C3E50] placeholder-[#9CA3AF] disabled:opacity-50"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#2C3E50] mb-1">Nom complet de l'admin</label>
          <input
            type="text"
            value={adminFullName}
            onChange={(e) => setAdminFullName(e?.target?.value)}
            placeholder="Prénom Nom"
            disabled={loading}
            className="w-full text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-[#2C3E50] placeholder-[#9CA3AF] disabled:opacity-50"
          />
        </div>

        <button
          onClick={handleProvision}
          disabled={loading || !isValid}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <Icon name="Loader2" size={14} className="animate-spin" />
          ) : (
            <Icon name="Send" size={14} />
          )}
          {loading ? 'Provisionnement…' : 'Provisionner'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-5 mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto flex-shrink-0">
            <Icon name="X" size={12} className="text-red-400" />
          </button>
        </div>
      )}

      {/* Success */}
      {result && (
        <div className="mx-5 mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <Icon name="CheckCircle2" size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-green-800">
            <span className="font-semibold">Invitation envoyée à {result?.email}</span>
            {' · '}
            <span className="font-mono text-green-700">org {result?.orgId}</span>
          </div>
          <button onClick={() => setResult(null)} className="ml-auto flex-shrink-0">
            <Icon name="X" size={12} className="text-green-500" />
          </button>
        </div>
      )}
    </div>
  );
};

const SuperAdminPage = () => {
  const { userProfile } = useAuth();

  // While auth is loading, render nothing
  if (userProfile === undefined || userProfile === null) return null;

  // Hard gate: redirect non-super-admins to home
  const email = (userProfile?.email ?? '')?.toLowerCase();
  if (!SUPER_ADMIN_EMAILS?.includes(email)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
            <Icon name="ShieldCheck" size={18} className="text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Super-Admin</h1>
        </div>
        <p className="text-sm text-[#6B7280] ml-12">
          Outils réservés aux super-administrateurs de la plateforme.
        </p>
      </div>

      <ProvisionTenantPanel userProfile={userProfile} />
    </div>
  );
};

export default SuperAdminPage;
